import React, { useState, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import * as Sentry from "@sentry/react";

const StepFlowNavigate = ({ stepParamName = "step", steps, initialFlowState, initialStepSlug }) => {
  // Check all steps have a slug
  steps.forEach((step) => {
    if (!step.slug) {
      throw new Error("Step does not have a slug");
    }
  });
  // Check all slugs are unique
  const slugs = steps.map((step) => step.slug);
  const uniqueSlugs = [...new Set(slugs)];
  if (slugs.length !== uniqueSlugs.length) {
    const duplicateSlugs = slugs.filter((item, index) => slugs.indexOf(item) !== index);
    throw new Error(`Steps do not have unique slugs - found duplicates: ${duplicateSlugs}`);
  }

  const [flowState, setFlowState] = useState(initialFlowState);

  // Ref tracks the latest flow state so onStepComplete can synchronously
  // read the most recent value before React 18 batching flushes the update.
  const flowStateRef = useRef(flowState);
  flowStateRef.current = flowState;

  const isStepValid = (step, nextState) => {
    if (step?.valid) {
      try {
        return step.valid(nextState);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: "StepFlowNavigate", action: "isStepValid" },
          extra: {
            stepSlug: step.slug,
            availableSlugs: slugs,
            flowStateKeys: Object.keys(nextState),
          },
        });
        throw error;
      }
    }
    return true;
  };

  // The named step is probed leniently: a throwing valid() is reported by
  // isStepValid and treated as unusable, so a stale resume link falls back
  // instead of crashing. The fallback scan below stays strict.
  const canStartAtNamedStep = (step) => {
    try {
      return isStepValid(step, initialFlowState);
    } catch {
      return false;
    }
  };

  // undefined, null and "" all mean no named step.
  const requestedSlug = initialStepSlug || null;

  const resolveInitialStep = () => {
    const namedIndex = requestedSlug ? slugs.indexOf(requestedSlug) : -1;

    if (namedIndex >= 0 && canStartAtNamedStep(steps[namedIndex])) {
      return { initialStepIndex: namedIndex, unresolvedInitialStepReason: null };
    }

    // The named step has already been evaluated, so skip it rather than
    // re-running (and re-reporting) a predicate that may have thrown.
    const fallbackIndex = steps.findIndex((step, index) => index !== namedIndex && isStepValid(step, initialFlowState));

    // Initialisation reports when no step is startable, so avoid a duplicate warning.
    if (!requestedSlug || fallbackIndex < 0) {
      return { initialStepIndex: fallbackIndex, unresolvedInitialStepReason: null };
    }

    return {
      initialStepIndex: fallbackIndex,
      unresolvedInitialStepReason: namedIndex < 0 ? "unknown-slug" : "step-not-valid",
    };
  };

  // Resolve once to keep showFirst aligned with maxReachedStepRef, which is
  // also initialised only on mount. Callers must provide both initial values
  // before mounting the flow; a later initialStepSlug is reported below and
  // otherwise ignored.
  const [{ initialStepIndex, unresolvedInitialStepReason }] = useState(resolveInitialStep);
  const initialSlug = steps[initialStepIndex]?.slug;

  // Tracks the furthest step legitimately reached in the current flow, used
  // by the beyondReached guard to catch browser-back into a completed flow's
  // history after showFirst has reset state. See Sentry CLIENT-APP-1HC.
  //
  // Invariant: any function that legitimately advances the user forward must
  // call `maxReachedStepRef.current = Math.max(maxReachedStepRef.current, i)`,
  // otherwise the guard will wrongly redirect them back. Currently advanced
  // by onStepComplete and showIndex; reset to initialStepIndex by showFirst.
  const maxReachedStepRef = useRef(initialStepIndex);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const stepSlug = searchParams.get(stepParamName);
  const currentStepIndex = steps.findIndex((step) => step.slug === stepSlug);

  const navigateToStep = (step, options = {}) => {
    setSearchParams((params) => {
      params.set(stepParamName, step.slug);
      return params;
    }, options);
  };

  useEffect(() => {
    if (unresolvedInitialStepReason == null) return;

    const diagnostics = {
      initialStepSlug: requestedSlug,
      reason: unresolvedInitialStepReason,
      availableSlugs: slugs,
      fallbackSlug: initialSlug,
      path: location.pathname,
    };
    console.warn(
      "StepFlowNavigate: initialStepSlug did not resolve, falling back to the first valid step",
      diagnostics,
    );
    Sentry.captureMessage("StepFlowNavigate: initialStepSlug did not resolve", {
      level: "warning",
      tags: { component: "StepFlowNavigate" },
      extra: diagnostics,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the reason is resolved once at mount, so this reports once; the other values are read once at mount for diagnostics only.
  }, [unresolvedInitialStepReason]);

  const lastRequestedSlugRef = useRef(requestedSlug);
  useEffect(() => {
    if (requestedSlug === lastRequestedSlugRef.current) return;

    const diagnostics = {
      previousInitialStepSlug: lastRequestedSlugRef.current,
      initialStepSlug: requestedSlug,
      resolvedInitialSlug: initialSlug,
      availableSlugs: slugs,
      path: location.pathname,
    };
    lastRequestedSlugRef.current = requestedSlug;
    console.warn(
      "StepFlowNavigate: initialStepSlug changed after mount, keeping the step resolved at mount",
      diagnostics,
    );
    Sentry.captureMessage("StepFlowNavigate: initialStepSlug changed after mount", {
      level: "warning",
      tags: { component: "StepFlowNavigate" },
      extra: diagnostics,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a change to requestedSlug should re-fire this; the other values are read for diagnostics only.
  }, [requestedSlug]);

  const [isInitialised, setIsInitialised] = useState(false);

  // Wait until the URL matches the initial step before rendering. This stops
  // stale links briefly opening a later step without its required state.
  // See Sentry CLIENT-APP-1HB.
  useEffect(() => {
    if (isInitialised) return;

    // Defensive: nowhere to start. No step's valid() passes for
    // initialFlowState, and any initialStepSlug could not be used either.
    if (initialStepIndex < 0 || initialSlug == null) {
      const message = "StepFlowNavigate: no valid initial step for initialFlowState";
      const diagnostics = {
        path: location.pathname,
        stepParamName,
        incomingSlug: stepSlug,
        initialStepSlug: requestedSlug,
        availableSlugs: slugs,
        initialStepIndex,
      };
      console.error(message, diagnostics);
      Sentry.captureMessage(message, {
        level: "error",
        tags: { component: "StepFlowNavigate" },
        extra: diagnostics,
      });
      setIsInitialised(true); // prevent the effect re-running; the currentStepIndex < 0 guard renders null
      return;
    }

    // Mark complete only when URL is actually at initial step
    if (stepSlug === initialSlug) {
      setIsInitialised(true);
      return;
    }

    // Request redirect; completion will be detected by stepSlug change
    navigateToStep(steps[initialStepIndex], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigateToStep is recreated every render (not memoised); including it would re-fire the effect each cycle. location and stepParamName are stable for a given route; slugs is derived from steps which is already in the dep list.
  }, [isInitialised, initialStepIndex, initialSlug, stepSlug, steps]);

  // After initialisation, if the URL step param becomes unrecognised
  // (currentStepIndex is -1), redirect back to the resolved initial step
  // rather than rendering a permanent blank screen.
  useEffect(() => {
    if (isInitialised && currentStepIndex < 0 && initialStepIndex >= 0) {
      const diagnostics = {
        unrecognisedSlug: stepSlug,
        availableSlugs: slugs,
        path: location.pathname,
      };
      console.warn("StepFlowNavigate: unrecognised slug after initialisation, redirecting", diagnostics);
      // A null slug means the URL has no step param, i.e. the bare entry path.
      // navigateToStep only ever sets a real slug, so a null here can only come
      // from a history navigation back to the entry (Safari surfaces the bare
      // URL on back where Chrome collapses it) — benign, and the redirect below
      // heals it. Only a non-null unrecognised slug (bad link, stale nav) is
      // worth reporting. See Sentry CLIENT-APP-1HV.
      if (stepSlug != null) {
        Sentry.captureMessage("StepFlowNavigate: unrecognised slug after initialisation", {
          level: "warning",
          tags: { component: "StepFlowNavigate" },
          extra: diagnostics,
        });
      }
      navigateToStep(steps[initialStepIndex], { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- navigateToStep is recreated every render (not memoised); location is stable for a given route; slugs is derived from steps which is already in the dep list.
  }, [isInitialised, currentStepIndex, initialStepIndex, stepSlug, steps]);

  // Redirect if the URL points beyond the furthest step legitimately reached.
  // This catches browser-back into old history entries after showFirst resets the flow.
  const beyondReached = isInitialised && currentStepIndex > maxReachedStepRef.current;
  useEffect(() => {
    if (!beyondReached) return;

    const diagnostics = {
      attemptedSlug: stepSlug,
      attemptedStepIndex: currentStepIndex,
      maxReachedStepIndex: maxReachedStepRef.current,
      availableSlugs: slugs,
      path: location.pathname,
    };

    // Defensive: if no step was ever legitimately reached, there is nowhere
    // to redirect to. The no-valid-initial-step handler above should have
    // already captured the underlying problem; surface the downstream effect
    // too so we can tell the two paths apart in Sentry.
    if (maxReachedStepRef.current < 0) {
      console.warn("StepFlowNavigate: beyond-reached but no reached step to redirect to", diagnostics);
      Sentry.captureMessage("StepFlowNavigate: beyond reached with no reached step", {
        level: "warning",
        tags: { component: "StepFlowNavigate" },
        extra: diagnostics,
      });
      return;
    }

    console.warn("StepFlowNavigate: redirecting from beyond-reached step", diagnostics);
    navigateToStep(steps[maxReachedStepRef.current], { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- beyondReached is the only signal that should re-fire this effect; the other values are read for diagnostics only, reflecting whichever render triggered the redirect.
  }, [beyondReached]);

  // onStepComplete is transactional: state and navigation are committed
  // together. If no valid next step exists, neither the stateDelta nor the
  // URL is changed — the caller's data is not silently persisted into a
  // flow that cannot advance. Callers that need guaranteed draft persistence
  // independent of navigation should use a separate API (e.g. autosave).
  // Returns { advanced: true } on success, or
  // { advanced: false, reason: 'no-valid-next-step' } when stuck.
  const onStepComplete = (stateDelta) => {
    const nextFlowState = { ...flowStateRef.current, ...stateDelta };

    let candidateStepIndex = currentStepIndex + 1;
    while (candidateStepIndex < steps.length) {
      const candidateStep = steps[candidateStepIndex];
      if (isStepValid(candidateStep, nextFlowState)) {
        maxReachedStepRef.current = Math.max(maxReachedStepRef.current, candidateStepIndex);
        flowStateRef.current = nextFlowState;
        setFlowState(nextFlowState);
        navigateToStep(candidateStep);
        return { advanced: true };
      }
      candidateStepIndex++;
    }

    const reason = "no-valid-next-step";
    const diagnostics = {
      reason,
      currentStepSlug: steps[currentStepIndex]?.slug,
      currentStepIndex,
      availableSlugs: slugs,
      flowStateKeys: Object.keys(nextFlowState),
    };
    console.warn("onStepComplete: reached end of step list, aborting", diagnostics);
    Sentry.captureMessage("onStepComplete: reached end of step list", {
      level: "warning",
      tags: { component: "StepFlowNavigate" },
      extra: diagnostics,
    });
    return { advanced: false, reason };
  };

  const showFirst = (partialflowState = {}) => {
    if (initialStepIndex < 0) {
      console.warn("showFirst called with no valid initial step");
      Sentry.captureMessage("showFirst called with no valid initial step", {
        level: "warning",
        tags: { component: "StepFlowNavigate" },
        extra: {
          path: location.pathname,
          stepParamName,
          stepSlug,
          initialStepIndex,
          currentStepIndex,
          stepsLength: steps.length,
        },
      });
      return;
    }
    const resetState = {
      ...initialFlowState,
      ...partialflowState,
    };
    flowStateRef.current = resetState;
    setFlowState(resetState);
    maxReachedStepRef.current = initialStepIndex;
    navigateToStep(steps[initialStepIndex]);
  };

  const showPrevious = () => {
    let candidateStepIndex = currentStepIndex - 1;

    while (candidateStepIndex >= 0) {
      const candidateStep = steps[candidateStepIndex];
      if (isStepValid(candidateStep, flowStateRef.current)) {
        navigateToStep(candidateStep);
        return;
      }
      candidateStepIndex--;
    }

    const diagnostics = {
      currentStepSlug: steps[currentStepIndex]?.slug,
      currentStepIndex,
      availableSlugs: slugs,
      flowStateKeys: Object.keys(flowStateRef.current),
    };
    console.warn("showPrevious: reached start of step list, aborting", diagnostics);
    Sentry.captureMessage("showPrevious: reached start of step list", {
      level: "warning",
      tags: { component: "StepFlowNavigate" },
      extra: diagnostics,
    });
  };

  const showIndex = (index) => {
    const step = steps[index];
    if (!step) {
      const diagnostics = {
        requestedIndex: index,
        stepCount: steps.length,
        availableSlugs: slugs,
      };
      console.warn("showIndex: index out of bounds", diagnostics);
      Sentry.captureMessage("showIndex: index out of bounds", {
        level: "warning",
        tags: { component: "StepFlowNavigate" },
        extra: diagnostics,
      });
      return;
    }
    if (!isStepValid(step, flowStateRef.current)) {
      const diagnostics = {
        requestedIndex: index,
        stepSlug: step.slug,
        stepCount: steps.length,
        availableSlugs: slugs,
        flowStateKeys: Object.keys(flowStateRef.current),
      };
      console.warn("showIndex: step is not valid for current flow state", diagnostics);
      Sentry.captureMessage("showIndex: step is not valid for current flow state", {
        level: "warning",
        tags: { component: "StepFlowNavigate" },
        extra: diagnostics,
      });
      return;
    }
    maxReachedStepRef.current = Math.max(maxReachedStepRef.current, index);
    navigateToStep(step);
  };

  if (currentStepIndex < 0) {
    // Pre-initialisation: no stepSlug yet, or an unrecognised slug. Render
    // nothing and let the initialisation effect redirect to the first valid step.
    // Post-initialisation: the unrecognised-slug effect (above) handles
    // redirecting back; this guard prevents a blank frame in the interim.
    return null;
  }

  // Suppress rendering while the beyondReached effect's redirect is in flight.
  if (beyondReached) {
    return null;
  }

  // Suppress rendering while the initialisation effect's redirect is in flight.
  // Without this, a stale URL like ?step=quote-sent would briefly render
  // that step's component with unpopulated flow state. See Sentry CLIENT-APP-1HB.
  if (!isInitialised && currentStepIndex !== initialStepIndex) {
    console.debug(`StepFlowNavigate: suppressing stale step "${stepSlug}" before initial redirect to "${initialSlug}"`);
    return null;
  }

  const activeStep = steps[currentStepIndex];

  let stepComponent;
  try {
    stepComponent = activeStep.component(flowState);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: "StepFlowNavigate", action: "renderStepComponent" },
      extra: {
        stepSlug: activeStep.slug,
        stepIndex: currentStepIndex,
        availableSlugs: slugs,
        flowStateKeys: Object.keys(flowState),
      },
    });
    throw error;
  }
  const renderComponent = React.cloneElement(stepComponent, {
    onStepComplete,
    showPrevious,
    showFirst,
    showIndex,
  });

  return renderComponent;
};

export default StepFlowNavigate;
